import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';
import moment from 'moment';
import Datetime from 'react-datetime';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import Field from './Field';
import 'react-datetime/css/react-datetime.css';
import Constants from '../../utils/Constants';
import useEffectOnLoad from '../../hooks/useEffectOnLoad';
import useCaretPosition from '../../hooks/useCaretPosition';
import { NO_STATE } from '../../hooks/useExtendedState';
import { extendedStateType } from '../../types';
import {
    resolveOnReset,
    resolveValue,
} from '../../utils/fields';

DateTimeField.propTypes = {
    label: PropTypes.node,
    value: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
    ]),
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    validator: PropTypes.func,
    onValidated: PropTypes.func,
    className: PropTypes.string,
    invalidBefore: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
    ]),
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
    timeFormat: PropTypes.node,
    dateFormat: PropTypes.node,
    isValidDate: PropTypes.func,
    initialViewDate: PropTypes.instanceOf(Date),
    validateOnLoad: PropTypes.bool,
    validateOnValidatorChange: PropTypes.bool,
    state: extendedStateType,
};

DateTimeField.defaultProps = {
    onChange: undefined,
    value: undefined,
    validator: null,
    onValidated: undefined,
    inputId: null,
    className: '',
    invalidBefore: null,
    onReset: undefined,
    isChanged: undefined,
    isRequired: false,
    timeFormat: 'HH:mm',
    label: null,
    dateFormat: 'DD.MM.YYYY',
    isValidDate: null,
    initialViewDate: null,
    validateOnLoad: false,
    validateOnValidatorChange: false,
    state: NO_STATE,
};

const hackishReset = (dateInputElement) => {
    // +HACK
    // it is not possible to pass an empty "value" to Datetime component when it already had some valid date value stored,
    // which means it cannot be simply reset to empty string, see https://github.com/arqex/react-datetime/issues/760
    // eslint-disable-next-line no-param-reassign
    dateInputElement.current.state.inputValue = '';
    // -HACK
};

export const valuesAreEqual = (first, second) => (
    first instanceof Date && second instanceof Date
        ? first.getTime() === second.getTime()
        : first === second
);

function DateTimeField(props) { // NOSONAR javascript:S3776
    const {
        label,
        inputId,
        value,
        onChange,
        className,
        validator,
        onValidated,
        invalidBefore,
        onReset,
        isChanged,
        isRequired,
        timeFormat,
        dateFormat,
        isValidDate,
        initialViewDate,
        validateOnLoad,
        validateOnValidatorChange,
        state,
    } = props;
    const { i18n } = useTranslation();
    const [error, setError] = useState(null);
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};
    const resolvedValue = resolveValue(value, state);
    const [displayedValue, setDisplayedValue] = useState(resolvedValue);
    const { ref: inputElement, onInput } = useCaretPosition(displayedValue);

    const debouncedValidation = useDebouncedCallback((valueForValidation) => {
        validator && validator(valueForValidation)
            .then(() => onValidationComplete(null, valueForValidation))
            .catch((e) => onValidationComplete(e, valueForValidation));
    }, Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS);
    const onValidationComplete = (newError, newValue) => {
        onValidated && onValidated(newValue, validator === null ? null : !newError);
        setError(newError);
    };
    const setNewValue = (newValue) => {
        if (!valuesAreEqual(newValue, displayedValue)) {
            setDisplayedValue(newValue);
            stateValueSetter(newValue);
            onChange && onChange(newValue);

            if (validator) {
                debouncedValidation(newValue);
            } else {
                onValidationComplete(null, newValue);
            }
        }
        if (newValue === null || newValue === '') {
            hackishReset(dateInputElement);
        }
    };
    const onChangeHandler = (newMoment) => {
        const newValue = typeof newMoment === 'object' ? newMoment.toDate() : newMoment;
        setNewValue(newValue);
    };
    useEffectOnUpdate(() => {
        const newResolvedValue = resolveValue(value, state);
        setNewValue(newResolvedValue);
    }, [value, state.value]);
    const isValidDateInternal = (currentDate) => {
        if (isValidDate) {
            return isValidDate(currentDate);
        }
        if (invalidBefore == null || !(invalidBefore instanceof Date)) {
            return true;
        }
        return currentDate.isAfter(moment(invalidBefore).subtract(1, 'day'));
    };
    const dateInputElement = useRef(null);
    useEffectOnUpdate(() => {
        const possibleInitialValueToSet = initialViewDate ? moment(initialViewDate) : moment(displayedValue);
        if (possibleInitialValueToSet.isValid()) {
            dateInputElement.current.setViewDate(possibleInitialValueToSet);
        }
    }, [initialViewDate, displayedValue]);
    useEffectOnLoad(() => {
        if (validateOnLoad) {
            const newResolvedValue = resolveValue(value, state);
            debouncedValidation(newResolvedValue);
        }
    });
    useEffectOnUpdate(() => {
        if (validateOnValidatorChange) {
            const newResolvedValue = resolveValue(value, state);
            debouncedValidation(newResolvedValue);
        }
    }, [validator]);

    return (
        <Field
            label={label}
            inputId={inputId}
            error={error}
            onReset={() => {
                hackishReset(dateInputElement);
                const resolvedOnReset = resolveOnReset(onReset, state);
                resolvedOnReset && resolvedOnReset();
            }}
            isChanged={isChanged}
            state={state}
            isRequired={isRequired && !displayedValue}
        >
            <Datetime
                ref={dateInputElement}
                inputProps={{
                    className,
                    onInput,
                    ref: inputElement,
                }}
                value={displayedValue}
                onChange={onChangeHandler}
                locale={i18n.language === 'en' ? 'en-GB' : i18n.language}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                isValidDate={isValidDateInternal}
                initialViewDate={initialViewDate}
            />
        </Field>
    );
}

export default memo(DateTimeField);
